import React from 'react';

import { useIsMobile } from 'src/application/hooks/useIsMobile';
import { FroalaEditorsSettings } from 'src/config/configs';

import { studentMessages } from '../../../../translates/studentMessages';
import { AudioCaptionsContextProvider } from '../../../components/Audio/AudioCaptionsContextProvider';
import { NotificationBar } from '../../../components/notification/NotificationBar';
import { getMainProjectPanorama } from '../../../globalPanoramaAccessor';
import { useIntlReexport } from '../../../hooks/useIntlReexport';
import { useSwitch } from '../../../hooks/useSwitch';
import { HistoryItem } from '../../../store/reducers/currentSequencedFlowPositionReducer';
import { useBindPanoramaWithStudentLinearFlowStep } from '../../Panorama/hooks/useBindPanoramaWithStudentLinearFlowStep';
import { MemoizedContentRequirementsProvider } from '../../RestrictNavigation/ContentRequirementsProvider';
import { MobileSequencedFlow } from '../containers/mobile/MobileSequencedFlow';
import { SequencedContentFlowContextProvider } from '../context/SequencedContentFlowContext';
import { DesktopSequencedFlow } from './DesktopSequencedFlow/DesktopSequencedFlow';
import { Section, SequencedFlowChildrenTypes, SequencedFlowItem } from '@ael/core-api';
import { List, Stack } from 'immutable';

import './SequencedContentFlow.scss';

export interface SequencedContentFlowProps {
  sequencedFlowItem: SequencedFlowItem | null;
  currentContentItem: SequencedFlowChildrenTypes | null;
  currentSectionItem: Section | SequencedFlowItem | null;
  history: Stack<HistoryItem>;
  sequencedContentFlowStructure: List<HistoryItem>;
  onGoToSection: (section: Section) => void;
  froalaEditorsSettings: FroalaEditorsSettings;
}

export const SequencedContentFlow: React.FC<SequencedContentFlowProps> = ({
  currentContentItem,
  currentSectionItem,
  onGoToSection,
  sequencedFlowItem,
  history,
  sequencedContentFlowStructure,
  froalaEditorsSettings,
}) => {
  const { formatMessage } = useIntlReexport();
  const globalPanorama = getMainProjectPanorama();
  useBindPanoramaWithStudentLinearFlowStep(globalPanorama, currentContentItem);
  const panelVisibility = useSwitch(true);

  const isMobile = useIsMobile();

  if (!currentSectionItem || !sequencedFlowItem) {
    return null;
  }

  if (!currentContentItem) {
    return <NotificationBar text={formatMessage(studentMessages.emptyContent)} />;
  }

  return (
    <AudioCaptionsContextProvider>
      <SequencedContentFlowContextProvider
        sequencedFlowItem={sequencedFlowItem}
        currentContentItem={currentContentItem}
        currentSection={currentSectionItem ? currentSectionItem : sequencedFlowItem}
        structure={sequencedContentFlowStructure}
      >
        {!!currentContentItem && (
          <MemoizedContentRequirementsProvider item={currentContentItem}>
            {!isMobile ? (
              <DesktopSequencedFlow
                currentContentItem={currentContentItem}
                onGoToSection={onGoToSection}
                history={history}
                panelVisibility={panelVisibility}
                froalaEditorsSettings={froalaEditorsSettings}
              />
            ) : (
              <>
                <MobileSequencedFlow
                  key={currentContentItem.id}
                  currentContentItem={currentContentItem}
                  onGoToSection={onGoToSection}
                  history={history}
                  froalaEditorsSettings={froalaEditorsSettings}
                />
              </>
            )}
          </MemoizedContentRequirementsProvider>
        )}
      </SequencedContentFlowContextProvider>
    </AudioCaptionsContextProvider>
  );
};
